import React from "react";
import { Icon } from "./registry";
/* ============================================================
   Fence Works — Permit Check (Wisconsin) — real rule engine
   Wisconsin Uniform Dwelling Code + common municipal fence rules.
   ============================================================ */
const PC_FT = "ft";

/* Wisconsin fence rules: statewide defaults + specific municipalities.
   Heights in feet. Sources: WI Uniform Dwelling Code + municipal ordinances. */
const WI_RULES = {
  _state: { name: "Wisconsin (state default)", residentialMax: 6, frontYardMax: 4, cornerVisionTriangle: 25, poolBarrierMin: 4, permitOverHeight: 6, setbackIn: 0, note: "Most WI municipalities cap residential fences at 6 ft (rear/side) and 4 ft in front yards. Pools require a self-latching barrier ≥4 ft (WI SPS 390)." },
  "green bay": { name: "Green Bay", residentialMax: 6, frontYardMax: 4, cornerVisionTriangle: 25, poolBarrierMin: 4, permitRequired: true, permitOverHeight: 0, fee: 50, note: "Green Bay requires a zoning permit for any fence. Corner lots: 25-ft vision triangle kept under 30 in." },
  "hobart": { name: "Hobart", residentialMax: 6, frontYardMax: 4, cornerVisionTriangle: 25, poolBarrierMin: 4, permitRequired: true, permitOverHeight: 0, fee: 40, note: "Village of Hobart requires a land-use permit for fences; rear/side 6 ft max, front 4 ft." },
  "madison": { name: "Madison", residentialMax: 6, frontYardMax: 4, cornerVisionTriangle: 30, poolBarrierMin: 5, permitRequired: true, permitOverHeight: 6, fee: 60, note: "Madison: permit required for fences over 6 ft; pool barriers must be ≥5 ft. Front-yard fences ≤4 ft." },
  "milwaukee": { name: "Milwaukee", residentialMax: 6, frontYardMax: 4, cornerVisionTriangle: 25, poolBarrierMin: 4, permitRequired: true, permitOverHeight: 0, fee: 75, note: "Milwaukee requires a permit for all new fences; corner-clearance and alley setbacks apply." },
  "appleton": { name: "Appleton", residentialMax: 6, frontYardMax: 4, cornerVisionTriangle: 25, poolBarrierMin: 4, permitRequired: false, permitOverHeight: 6, fee: 0, note: "Appleton: no permit under 6 ft in rear/side yards; front-yard fences limited to 4 ft and 50% open." },
};

/* "123 Hobart Rd, Green Bay, WI 54301" is Green Bay, not Hobart: match the
   locality segment (the last comma-separated part, once a trailing "WI 54301"
   is dropped) first, longest municipality name first; then fall back to the
   longest name found anywhere in the text; then the state default. */
function lookupWiRule(text) {
  const t = String(text || "").toLowerCase();
  const keys = Object.keys(WI_RULES).filter(k => k !== "_state").sort((a, b) => b.length - a.length);
  const parts = t.split(",").map(s => s.trim()).filter(Boolean);
  while (parts.length > 1 && /^(wi|wisconsin)\b[\s\d-]*$/.test(parts[parts.length - 1])) parts.pop();
  const locality = parts.length ? parts[parts.length - 1].replace(/\b(wi|wisconsin)\b[\s\d-]*$/, "").trim() : "";
  const local = locality ? keys.find(k => locality.includes(k)) : null;
  if (local) return WI_RULES[local];
  const any = keys.find(k => t.includes(k));
  return any ? WI_RULES[any] : WI_RULES._state;
}

/* the rules above are Wisconsin-only — if the address names another state,
   say so instead of silently grading it against WI code. Two-letter codes
   only match UPPERCASE (", FL") so words like "or"/"in" don't false-trip. */
const OTHER_STATE_ABBR = /\b(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WY)\b/;
const OTHER_STATE_NAME = /\b(florida|illinois|minnesota|michigan|iowa|texas|california|colorado|arizona|georgia|ohio|indiana|missouri|tennessee|kentucky)\b/i;
function outOfStateHint(addr) {
  const t = String(addr || "");
  if (!t.trim() || /\bWI\b/.test(t) || /wisconsin/i.test(t)) return null;
  const m = t.match(OTHER_STATE_ABBR) || t.match(OTHER_STATE_NAME);
  return m ? m[0].toUpperCase() : null;
}

function PermitCheck({ store, go }: any) {
  const [address, setAddress] = React.useState<any>("");
  const [height, setHeight] = React.useState<any>(6);
  const [pool, setPool] = React.useState<any>(false);
  const [corner, setCorner] = React.useState<any>(false);
  const [frontYard, setFrontYard] = React.useState<any>(false);
  const [result, setResult] = React.useState<any>(null);

  function run() {
    const rule = lookupWiRule(address);
    const checks = [];
    const maxH = frontYard ? rule.frontYardMax : rule.residentialMax;
    checks.push({
      ok: height <= maxH,
      label: `Height ${height} ft vs ${frontYard ? "front-yard" : "rear/side"} max ${maxH} ft`,
      detail: height <= maxH ? "Within limit" : `Exceeds the ${maxH} ft limit — variance likely required`,
    });
    const needsPermit = rule.permitRequired || (rule.permitOverHeight > 0 && height > rule.permitOverHeight);
    checks.push({
      ok: !needsPermit, warn: needsPermit,
      label: needsPermit ? "Permit required" : "No permit required",
      detail: needsPermit ? `${rule.name} requires a fence permit${rule.fee ? ` (~$${rule.fee})` : ""}. File before install.` : "Standard residential fence — no permit needed at this height.",
    });
    if (corner) checks.push({ ok: true, warn: true, label: "Corner-lot vision triangle", detail: `Keep the fence under 30 in within ${rule.cornerVisionTriangle} ft of the intersection.` });
    if (pool) checks.push({ ok: height >= rule.poolBarrierMin, warn: height < rule.poolBarrierMin, label: "Pool barrier", detail: height >= rule.poolBarrierMin ? `Meets the ${rule.poolBarrierMin} ft pool-barrier minimum (self-latching gate required).` : `Pool barrier must be ≥ ${rule.poolBarrierMin} ft with a self-latching, self-closing gate (WI SPS 390).` });
    setResult({ rule, checks, needsPermit, maxH });
  }

  return (
    <div className="page fade-up">
      <div className="row gap-8" style={{ marginBottom: 4, alignItems: "center" }}>
        <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Permit Check</h1>
        <span className="chip teal" style={{ fontSize: 10.5 }}>Wisconsin</span>
      </div>
      <p className="muted" style={{ margin: "4px 0 20px" }}>Check fence height limits, permit requirements, corner-vision and pool-barrier rules against Wisconsin code &amp; local ordinances before you quote.</p>

      <div className="settings-grid">
        <main style={{ display: "grid", gap: 16 }}>
          <div className="card pad">
            <div className="row gap-8 kicker kicker-teal" style={{ marginBottom: 14 }}><Icon name="mappin" size={14} />Property &amp; Fence</div>
            <label className="field-label">Municipality or address (Wisconsin)</label>
            <input className="input" value={address} onChange={e => setAddress(e.target.value)} placeholder="e.g. Green Bay, Hobart, Madison, Milwaukee, Appleton…" />
            {outOfStateHint(address) && (
              <p style={{ color: "var(--amber)", fontSize: 12.5, margin: "8px 0 0" }}>
                <Icon name="bell" size={12} /> That looks like {outOfStateHint(address)} — this checker covers <b>Wisconsin</b> rules only for now, so treat the results as a WI reference and verify with the local jurisdiction.
              </p>
            )}
            <div className="row gap-12" style={{ marginTop: 12, flexWrap: "wrap" }}>
              <div style={{ minWidth: 130 }}>
                <label className="field-label">Fence height (ft)</label>
                <input className="input tnum" type="number" min="1" max="12" step="0.5" value={height} onChange={e => setHeight(+e.target.value || 0)} />
              </div>
            </div>
            <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
              <Toggle2 on={frontYard} set={setFrontYard} label="Front-yard fence" note="stricter height limit" />
              <Toggle2 on={corner} set={setCorner} label="Corner lot" note="vision-triangle rule" />
              <Toggle2 on={pool} set={setPool} label="Pool enclosure" note="barrier code applies" />
            </div>
            <button className="btn btn-primary btn-block" style={{ marginTop: 16 }} onClick={run}><Icon name="check" size={15} />Check requirements</button>
          </div>

          {result && (
            <div className="card pad fade-up">
              <div className="row between" style={{ marginBottom: 14 }}>
                <div className="row gap-8 kicker kicker-teal"><Icon name="clipboard" size={14} />Result · {result.rule.name}</div>
                <span className={"chip " + (result.needsPermit ? "amber" : "green")}>{result.needsPermit ? "Permit required" : "No permit"}</span>
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {result.checks.map((c, i) => (
                  <div key={i} className="pc-check" style={{ borderColor: c.ok ? "rgba(52,211,153,.3)" : c.warn ? "rgba(245,196,81,.3)" : "rgba(240,99,99,.3)" }}>
                    <Icon name={c.ok ? "check" : c.warn ? "bolt" : "x"} size={15} style={{ color: c.ok ? "var(--green)" : c.warn ? "var(--amber)" : "var(--red)", flexShrink: 0, marginTop: 1 }} />
                    <div><div style={{ fontWeight: 700, fontSize: 13.5 }}>{c.label}</div><div className="muted" style={{ fontSize: 12.5 }}>{c.detail}</div></div>
                  </div>
                ))}
              </div>
              <div className="pe-callout info" style={{ marginTop: 14 }}>
                <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>{result.rule.note}</p>
              </div>
            </div>
          )}
        </main>

        <aside className="set-side">
          <div className="card pad">
            <div className="kicker" style={{ marginBottom: 10 }}>Wisconsin coverage</div>
            {Object.keys(WI_RULES).filter(k => k !== "_state").map(k => (
              <div key={k} className="row between" style={{ padding: "6px 0", fontSize: 12.5, borderBottom: "1px solid var(--line)" }}>
                <span style={{ textTransform: "capitalize" }}>{WI_RULES[k].name}</span>
                <span className="mono faint">{WI_RULES[k].residentialMax} ft</span>
              </div>
            ))}
            <p className="faint" style={{ fontSize: 11, marginTop: 10 }}>Other WI addresses use the statewide default. More states &amp; live municipal APIs coming next.</p>
          </div>
          <div className="card pad">
            <div className="kicker" style={{ marginBottom: 8 }}>Why it matters</div>
            <p className="muted" style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5 }}>Catching a height or permit issue before the quote saves a callback, a re-pull, and an unhappy customer. Add the permit fee to the estimate as a pass-through.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Toggle2({ on, set, label, note }: any) {
  return (
    <button className={"toggle-row" + (on ? " on" : "")} onClick={() => set(!on)}>
      <span className={"sw" + (on ? " on" : "")}><span className="knob" /></span>
      <span style={{ fontSize: 13.5, fontWeight: 600 }}>{label}</span>
      <span className="muted" style={{ fontSize: 12, marginLeft: "auto" }}>{note}</span>
    </button>
  );
}

Object.assign(window, { PermitCheck, WI_RULES, lookupWiRule });

export { lookupWiRule, PermitCheck, Toggle2, PC_FT, WI_RULES };
